import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import {
  Pill, FileText, CheckCircle, AlertTriangle, Clock, Send,
  Plus, ChevronRight, X, Edit3, Pause, Trash2, Eye, Upload, Download,
} from "lucide-react";
import {
  Btn, OspiteAvatar, StatoBadge, DetailPanel, formatDate, formatDateTime,
  KpiCard, FilterBar,
} from "../ricette/shared";
import { sortTerapieByOrarioNome } from "../../../utils/terapieExtraFormat";
import CambioTerapiaDialog from "../../../components/fascicoli/CambioTerapiaDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/deblistering/terapie`;
function hdrs(token) { return { Authorization: `Bearer ${token}` }; }
async function apiGet(path, token) {
  const r = await axios.get(`${BASE}${path}`, { headers: hdrs(token) });
  return r.data;
}
async function apiPost(path, token, data) {
  const r = await axios.post(`${BASE}${path}`, data, { headers: hdrs(token) });
  return r.data;
}
async function apiDelete(path, token) {
  const r = await axios.delete(`${BASE}${path}`, { headers: hdrs(token) });
  return r.data;
}
async function apiPut(path, token, data) {
  const r = await axios.put(`${BASE}${path}`, data, { headers: hdrs(token) });
  return r.data;
}

const STATO_OSPITE_CONFIG = {
  anomalia:         { label: "Anomalia",         dot: "bg-red-500",    ring: "ring-red-200",    text: "text-red-700",    bg: "bg-red-50"   },
  da_validare:      { label: "Da validare",       dot: "bg-amber-500",  ring: "ring-amber-200",  text: "text-amber-700",  bg: "bg-amber-50" },
  in_attesa_documento: { label: "In attesa doc",  dot: "bg-slate-400",  ring: "ring-slate-200",  text: "text-slate-600",  bg: "bg-slate-50" },
  sincronizzata:    { label: "Sincronizzata",     dot: "bg-emerald-500",ring: "ring-emerald-200",text: "text-emerald-700",bg: "bg-emerald-50"},
  da_verificare:    { label: "Da verificare",     dot: "bg-amber-500",  ring: "ring-amber-200",  text: "text-amber-700",  bg: "bg-amber-50" },
};

const TIPO_MODIFICA_CONFIG = {
  nuovo_farmaco:    { label: "Nuovo farmaco",     color: "bg-blue-50 text-blue-700 border-blue-200"    },
  farmaco_eliminato:{ label: "Da rimuovere",      color: "bg-red-50 text-red-700 border-red-200"       },
  dosaggio_modificato:{ label: "Dosaggio cambiato", color: "bg-amber-50 text-amber-700 border-amber-200"},
  frequenza_modificata:{ label: "Freq. cambiata", color: "bg-violet-50 text-violet-700 border-violet-200"},
  invariato:        { label: "Invariato",         color: "bg-slate-50 text-slate-600 border-slate-200"  },
};

function TipoBadge({ tipo }) {
  const cfg = TIPO_MODIFICA_CONFIG[tipo] || TIPO_MODIFICA_CONFIG.invariato;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-semibold border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function StatoOspiteBadge({ stato }) {
  const cfg = STATO_OSPITE_CONFIG[stato] || STATO_OSPITE_CONFIG.sincronizzata;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ==================== MAIN COMPONENT ====================

export default function TerapieDeblistering({ token }) {
  const { activeStruttura } = useAuth();
  const [ospiti, setOspiti] = useState([]);
  const [loadingOspiti, setLoadingOspiti] = useState(true);
  const [selectedOspite, setSelectedOspite] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [activeTab, setActiveTab] = useState("terapia_attiva");
  const [showRichiediDialog, setShowRichiediDialog] = useState(false);

  const loadOspiti = useCallback(async () => {
    setLoadingOspiti(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterStato) params.append("stato_filter", filterStato);
      const data = await apiGet(`/ospiti?${params}`, token);
      setOspiti(data.items || []);
    } catch {
      toast.error("Errore caricamento ospiti");
    } finally {
      setLoadingOspiti(false);
    }
  }, [token, search, filterStato, activeStruttura?.id]);

  useEffect(() => {
    setSelectedOspite(null);
    loadOspiti();
  }, [loadOspiti]);

  const stats = {
    totale: ospiti.length,
    da_validare: ospiti.filter(o => ["da_validare","anomalia"].includes(o.terapia_stato)).length,
    sincronizzata: ospiti.filter(o => o.terapia_stato === "sincronizzata").length,
    in_attesa: ospiti.filter(o => o.terapia_stato === "in_attesa_documento").length,
  };

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Pill}          label="Ospiti con terapia"  value={stats.totale}       tone="violet" />
        <KpiCard icon={AlertTriangle} label="Da validare"         value={stats.da_validare}  tone="amber"  />
        <KpiCard icon={CheckCircle}   label="Sincronizzate"       value={stats.sincronizzata} tone="emerald"/>
        <KpiCard icon={Clock}         label="In attesa documento" value={stats.in_attesa}    tone="slate"  />
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* Lista ospiti */}
        <div className="lg:w-72 xl:w-80 shrink-0">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <FilterBar
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Cerca ospite..."
                filters={
                  <select
                    value={filterStato}
                    onChange={e => setFilterStato(e.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-600 shadow-sm focus:outline-none"
                  >
                    <option value="">Tutti gli stati</option>
                    {Object.entries(STATO_OSPITE_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                }
              />
            </div>
            {loadingOspiti ? (
              <div className="py-10 text-center text-slate-400 text-sm">Caricamento...</div>
            ) : ospiti.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Nessun ospite trovato</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                {ospiti.map(o => (
                  <button
                    key={o.fascicolo_id}
                    type="button"
                    onClick={() => { setSelectedOspite(o); setActiveTab("terapia_attiva"); }}
                    className={`w-full text-left px-4 py-3 transition hover:bg-violet-50 flex items-center gap-3 ${selectedOspite?.fascicolo_id === o.fascicolo_id ? "bg-violet-50 border-l-2 border-violet-600" : ""}`}
                  >
                    <OspiteAvatar cognome={o.cognome} nome={o.nome} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{o.cognome} {o.nome}</p>
                      {o.camera && <p className="text-xs text-slate-400">Camera {o.camera}</p>}
                      <div className="mt-1">
                        <StatoOspiteBadge stato={o.terapia_stato} />
                      </div>
                      {o.proposte_pendenti > 0 && (
                        <span className="mt-0.5 inline-block text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                          {o.proposte_pendenti} proposta/e
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Pannello dettaglio */}
        {selectedOspite ? (
          <div className="flex-1 min-w-0">
            <OspiteDetailPanel
              ospite={selectedOspite}
              token={token}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onRefresh={loadOspiti}
              onRequestTherapy={() => setShowRichiediDialog(true)}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[300px] bg-white rounded-xl border border-slate-200 border-dashed text-slate-400 text-sm">
            Seleziona un ospite per visualizzare le terapie
          </div>
        )}
      </div>

      {/* Dialog Richiedi Terapia */}
      {showRichiediDialog && selectedOspite && (
        <RichiediTerapiaDialog
          ospite={selectedOspite}
          token={token}
          onClose={() => setShowRichiediDialog(false)}
          onSuccess={() => { setShowRichiediDialog(false); setActiveTab("richieste"); loadOspiti(); }}
        />
      )}
    </div>
  );
}

// ==================== OSPITE DETAIL PANEL ====================

function OspiteDetailPanel({ ospite, token, activeTab, setActiveTab, onRefresh, onRequestTherapy }) {
  const tabs = [
    { id: "terapia_attiva", label: "Terapia attiva", icon: Pill },
    { id: "da_validare",    label: "Da validare",    icon: AlertTriangle, badge: ospite.proposte_pendenti || 0 },
    { id: "documenti",      label: "Terapia iniziale", icon: FileText },
    { id: "cambio_storico", label: "Cambio terapia",  icon: Clock },
    { id: "richieste",      label: "Richieste inviate", icon: Send },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Ospite header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <OspiteAvatar cognome={ospite.cognome} nome={ospite.nome} className="w-10 h-10" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900">{ospite.cognome} {ospite.nome}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            <StatoOspiteBadge stato={ospite.terapia_stato} />
            {ospite.camera && <span className="text-xs text-slate-400">Camera {ospite.camera}</span>}
          </div>
        </div>
        <Btn variant="soft" onClick={onRequestTherapy} className="shrink-0">
          <Send className="w-4 h-4" />
          Richiedi terapia
        </Btn>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-0 border-b border-slate-100 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${
              activeTab === t.id
                ? "border-violet-600 text-violet-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
            {t.badge > 0 && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded-full font-bold">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-5">
        {activeTab === "terapia_attiva"  && <TerapiaAttivaTab ospite={ospite} token={token} onRefresh={onRefresh} />}
        {activeTab === "da_validare"     && <TerapieValidazioneTab ospite={ospite} token={token} onRefresh={onRefresh} />}
        {activeTab === "documenti"       && <DocumentiTerapiaTab ospite={ospite} token={token} />}
        {activeTab === "cambio_storico"  && <CambioTerapiaTab ospite={ospite} token={token} />}
        {activeTab === "richieste"       && <RichiesteInviateTab ospite={ospite} token={token} onRequestTherapy={onRequestTherapy} />}
      </div>
    </div>
  );
}

// ==================== TERAPIA ATTIVA TAB ====================

function TerapiaAttivaTab({ ospite, token, onRefresh }) {
  const [terapie, setTerapie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/ospiti/${ospite.fascicolo_id}/terapia-attiva`, token);
      setTerapie(data.items || []);
    } catch { toast.error("Errore caricamento terapia"); }
    finally { setLoading(false); }
  }, [ospite.fascicolo_id, token]);

  useEffect(() => { load(); }, [load]);

  const attive = sortTerapieByOrarioNome(terapie.filter(t => t.attiva !== false));
  const sospese = sortTerapieByOrarioNome(terapie.filter(t => t.attiva === false));

  async function handleSospendi(id) {
    if (!window.confirm("Sospendere questa terapia?")) return;
    try {
      await apiPut(`/ospiti/${ospite.fascicolo_id}/terapia-attiva/${id}`, token, { attiva: false });
      toast.success("Terapia sospesa");
      load(); onRefresh();
    } catch { toast.error("Errore sospensione"); }
  }

  async function handleElimina(id) {
    if (!window.confirm("Rimuovere questa terapia? L'operazione non è reversibile.")) return;
    try {
      await apiDelete(`/ospiti/${ospite.fascicolo_id}/terapia-attiva/${id}`, token);
      toast.success("Terapia rimossa");
      load(); onRefresh();
    } catch { toast.error("Errore rimozione"); }
  }

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>;

  return (
    <div className="space-y-4">
      {attive.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">Nessuna terapia attiva</div>
      ) : (
        <div className="grid gap-3">
          {attive.map(te => (
            <TerapiaCard
              key={te.id}
              te={te}
              onSospendi={() => handleSospendi(te.id)}
              onElimina={() => handleElimina(te.id)}
              onEdit={() => setEditingId(te.id)}
            />
          ))}
        </div>
      )}
      {sospese.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-slate-400 cursor-pointer select-none">
            {sospese.length} terapia/e sospesa/e
          </summary>
          <div className="grid gap-2 mt-2">
            {sospese.map(te => (
              <div key={te.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50 opacity-60">
                <span className="font-medium text-slate-700 text-sm">{te.nome}</span>
                {te.dosaggio && <span className="text-xs text-slate-400 ml-2">{te.dosaggio}</span>}
                {te.data_fine && <span className="text-xs text-slate-400 ml-2">Sospesa il {formatDate(te.data_fine)}</span>}
              </div>
            ))}
          </div>
        </details>
      )}

      {editingId && (
        <EditTerapiaModal
          te={terapie.find(t => t.id === editingId)}
          fascicolo_id={ospite.fascicolo_id}
          token={token}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); load(); onRefresh(); }}
        />
      )}
    </div>
  );
}

function TerapiaCard({ te, onSospendi, onElimina, onEdit }) {
  return (
    <div className="border border-slate-200 rounded-xl p-4 flex items-start gap-3 hover:border-slate-300 transition">
      <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
        <Pill className="w-4 h-4 text-violet-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 text-sm">{te.nome}</p>
        <div className="flex flex-wrap gap-2 mt-1">
          {te.dosaggio && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">{te.dosaggio}</span>}
          {te.orari?.length > 0 && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md">{te.orari.join(" / ")}</span>}
          {te.principio_attivo && <span className="text-xs bg-violet-50 text-violet-600 px-2 py-0.5 rounded-md" title="Principio attivo">{te.principio_attivo}</span>}
        </div>
        {te.note && <p className="text-xs text-slate-400 mt-1 truncate">{te.note}</p>}
        <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-slate-400">
          {te.data_inizio && <span>Dal {formatDate(te.data_inizio)}</span>}
          {te.origine && <span className="bg-slate-100 text-slate-500 px-1.5 rounded">{te.origine}</span>}
          {te.data_sincronizzazione && <span>Sync {formatDate(te.data_sincronizzazione)}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button type="button" onClick={onEdit} className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition" title="Modifica">
          <Edit3 className="w-4 h-4" />
        </button>
        <button type="button" onClick={onSospendi} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition" title="Sospendi">
          <Pause className="w-4 h-4" />
        </button>
        <button type="button" onClick={onElimina} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Elimina">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function EditTerapiaModal({ te, fascicolo_id, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    nome: te?.nome || "",
    dosaggio: te?.dosaggio || "",
    note: te?.note || "",
    data_fine: te?.data_fine || "",
    orari: (te?.orari || []).join(", "),
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const orari = form.orari ? form.orari.split(",").map(s => s.trim()).filter(Boolean) : [];
      await apiPut(`/ospiti/${fascicolo_id}/terapia-attiva/${te.id}`, token, {
        nome: form.nome, dosaggio: form.dosaggio, note: form.note,
        data_fine: form.data_fine || null, orari,
      });
      toast.success("Terapia aggiornata");
      onSaved();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h4 className="font-semibold text-slate-900">Modifica terapia</h4>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {[["Farmaco", "nome"], ["Dosaggio", "dosaggio"], ["Note", "note"], ["Orari (es. 08:00, 20:00)", "orari"]].map(([label, key]) => (
            <div key={key}>
              <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
              <input
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                value={form[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <Btn variant="secondary" onClick={onClose}>Annulla</Btn>
          <Btn onClick={handleSave} disabled={saving}>{saving ? "Salvo..." : "Salva"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ==================== MODIFICA PROPOSTA MODAL ====================

function EditPropostaModal({ proposta, fascicolo_id, token, onClose, onSaved }) {
  const [form, setForm] = useState({
    farmaco: proposta.farmaco || "",
    dosaggio: proposta.dosaggio || proposta.valore_proposto || "",
    frequenza: proposta.frequenza || "",
    note: proposta.note || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await axios.patch(
        `${BASE}/ospiti/${fascicolo_id}/proposte/${proposta.id}`,
        { ...form, valore_proposto: form.dosaggio },
        { headers: hdrs(token) },
      );
      toast.success("Proposta modificata");
      onSaved(r.data.proposta);
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-xl">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Modifica proposta</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Farmaco</label>
            <input className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.farmaco} onChange={e => setForm(f => ({ ...f, farmaco: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Dosaggio / Valore proposto</label>
            <input className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.dosaggio} onChange={e => setForm(f => ({ ...f, dosaggio: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Frequenza</label>
            <input className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" value={form.frequenza} onChange={e => setForm(f => ({ ...f, frequenza: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Note</label>
            <textarea className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="secondary" onClick={onClose}>Annulla</Btn>
            <Btn onClick={handleSave} disabled={saving}>
              {saving ? "Salvataggio..." : "Salva modifiche"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== TERAPIE DA VALIDARE TAB ====================

function TerapieValidazioneTab({ ospite, token, onRefresh }) {
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [approvando, setApprovando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [editProposta, setEditProposta] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/ospiti/${ospite.fascicolo_id}/proposte`, token);
      setProposte(data.items || []);
      setSelected([]);
    } catch { toast.error("Errore caricamento proposte"); }
    finally { setLoading(false); }
  }, [ospite.fascicolo_id, token]);

  useEffect(() => { load(); }, [load]);

  function toggleSelect(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  async function handleApprova() {
    if (selected.length === 0) return;
    setApprovando(true);
    try {
      const res = await apiPost(`/ospiti/${ospite.fascicolo_id}/proposte/approva-batch`, token, {
        proposta_ids: selected,
      });
      toast.success(`${res.approvate || 0} terapie approvate, ${res.sospese || 0} sospese`);
      load(); onRefresh();
    } catch { toast.error("Errore approvazione"); }
    finally { setApprovando(false); }
  }

  async function handleRifiuta(id) {
    if (!window.confirm("Rifiutare questa proposta?")) return;
    try {
      await apiDelete(`/ospiti/${ospite.fascicolo_id}/proposte/${id}`, token);
      toast.success("Proposta rifiutata");
      load(); onRefresh();
    } catch { toast.error("Errore rifiuto"); }
  }

  async function handleEliminaSelezionate() {
    if (selected.length === 0) return;
    const label = selected.length === 1 ? "la proposta selezionata" : `le ${selected.length} proposte selezionate`;
    if (!window.confirm(`Eliminare ${label}?`)) return;
    setEliminando(true);
    try {
      const results = await Promise.allSettled(
        selected.map((id) => apiDelete(`/ospiti/${ospite.fascicolo_id}/proposte/${id}`, token)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (fail === 0) {
        toast.success(ok === 1 ? "Proposta eliminata" : `${ok} proposte eliminate`);
      } else {
        toast.warning(`${ok} eliminate, ${fail} non riuscite`);
      }
      load(); onRefresh();
    } catch {
      toast.error("Errore eliminazione");
    } finally {
      setEliminando(false);
    }
  }

  const handlePropostaSaved = (updated) => {
    setProposte(ps => ps.map(p => p.id === updated.id ? updated : p));
    setEditProposta(null);
  };

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>;
  if (proposte.length === 0) return (
    <div className="py-8 text-center text-slate-400 text-sm">Nessuna proposta da validare</div>
  );

  const allSelected = proposte.every(p => selected.includes(p.id));

  return (
    <div className="space-y-4">
      {editProposta && (
        <EditPropostaModal
          proposta={editProposta}
          fascicolo_id={ospite.fascicolo_id}
          token={token}
          onClose={() => setEditProposta(null)}
          onSaved={handlePropostaSaved}
        />
      )}
      {/* Azioni batch */}
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={() => setSelected(allSelected ? [] : proposte.map(p => p.id))}
            className="rounded border-slate-300"
          />
          {selected.length > 0 ? `${selected.length} selezionate` : "Seleziona tutto"}
        </label>
        <div className="flex gap-2">
          {selected.length > 0 && (
            <>
              <Btn onClick={handleEliminaSelezionate} disabled={eliminando || approvando} variant="danger">
                <Trash2 className="w-4 h-4" />
                {eliminando ? "Eliminazione..." : `Elimina (${selected.length})`}
              </Btn>
              <Btn onClick={handleApprova} disabled={approvando || eliminando} variant="success">
                <CheckCircle className="w-4 h-4" />
                {approvando ? "Approvazione..." : `Approva e sincronizza (${selected.length})`}
              </Btn>
            </>
          )}
        </div>
      </div>

      {/* Lista proposte con diff */}
      <div className="space-y-3">
        {proposte.map(p => (
          <PropostaCard
            key={p.id}
            proposta={p}
            selected={selected.includes(p.id)}
            onToggle={() => toggleSelect(p.id)}
            onRifiuta={() => handleRifiuta(p.id)}
            onModifica={() => setEditProposta(p)}
          />
        ))}
      </div>
    </div>
  );
}

function PropostaCard({ proposta: p, selected, onToggle, onRifiuta, onModifica }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`border rounded-xl transition ${selected ? "border-violet-400 bg-violet-50/30" : "border-slate-200 bg-white"}`}>
      <div className="flex items-start gap-3 p-4">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="mt-1 rounded border-slate-300"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm">{p.farmaco}</span>
            <TipoBadge tipo={p.tipo_modifica} />
          </div>
          {/* Diff Prima / Proposto */}
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
              <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-1">Prima</p>
              <p className="text-sm text-slate-700">{p.valore_prima || <span className="italic text-slate-400">—</span>}</p>
            </div>
            <div className={`border rounded-lg p-2.5 ${
              p.tipo_modifica === "farmaco_eliminato"  ? "bg-red-50 border-red-200" :
              p.tipo_modifica === "nuovo_farmaco"      ? "bg-blue-50 border-blue-200" :
              p.tipo_modifica === "dosaggio_modificato"? "bg-amber-50 border-amber-200" :
              "bg-slate-50 border-slate-200"
            }`}>
              <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mb-1">Proposto</p>
              <p className="text-sm text-slate-700">
                {p.tipo_modifica === "farmaco_eliminato"
                  ? <span className="text-red-600">Rimuovere</span>
                  : (p.valore_proposto || p.dosaggio || <span className="italic text-slate-400">—</span>)}
              </p>
            </div>
          </div>
          {p.frequenza && (
            <p className="text-xs text-slate-400 mt-1.5">Frequenza: {p.frequenza}</p>
          )}
          {p.note && (
            <p className="text-xs text-slate-500 mt-1 italic">{p.note}</p>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
            <span>Fonte: {p.source === "deblistering_vision" ? "Analisi documento" : p.source === "deblistering_ocr" ? "OCR" : p.source || "—"}</span>
            <span>Ricevuto {formatDateTime(p.created_at)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => setExpanded(e => !e)} className="p-1.5 text-slate-400 hover:text-violet-600 rounded-lg transition" title="Dettaglio">
            <Eye className="w-4 h-4" />
          </button>
          <button type="button" onClick={onModifica} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Modifica proposta">
            <Edit3 className="w-4 h-4" />
          </button>
          <button type="button" onClick={onRifiuta} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Rifiuta">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-0">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-500 space-y-1">
            <p><strong>ID proposta:</strong> {p.id}</p>
            <p><strong>Documento:</strong> {p.source_document_id || "—"}</p>
            {p.source && <p><strong>Metodo estrazione:</strong> {p.source}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== DOCUMENTI TERAPIA TAB ====================

function DocumentiTerapiaTab({ ospite, token }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await apiGet(`/ospiti/${ospite.fascicolo_id}/documenti`, token);
        setDocs(data.items || []);
      } catch { toast.error("Errore caricamento documenti"); }
      finally { setLoading(false); }
    })();
  }, [ospite.fascicolo_id, token]);

  const iniziali = docs.filter(d => d.tipo === "terapia_iniziale");

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>;
  if (iniziali.length === 0) return (
    <div className="py-8 text-center text-slate-400 text-sm">Nessun documento terapia iniziale</div>
  );

  return (
    <div className="space-y-3">
      {iniziali.map(d => (
        <div key={d.id} className="border border-slate-200 rounded-xl p-4 flex items-start gap-3">
          <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 text-sm truncate">{d.file_name}</p>
            <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-400">
              <span>Origine: <span className="font-medium text-slate-600">{d.origine || "—"}</span></span>
              {d.caricato_da_nome && <span>Caricato da: {d.caricato_da_nome}</span>}
              <span>{formatDate(d.createdAt)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== CAMBIO TERAPIA TAB ====================

function CambioTerapiaTab({ ospite, token }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/ospiti/${ospite.fascicolo_id}/documenti`, token);
      setDocs((data.items || []).filter(d => d.tipo === "cambio_terapia"));
    } catch { toast.error("Errore caricamento storico"); }
    finally { setLoading(false); }
  }, [ospite.fascicolo_id, token]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleDownload = async (docId, fileName) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/deblistering/documenti/${docId}/download`,
        { headers: hdrs(token), responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "cambio_terapia.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore download");
    }
  };

  const enteLabel = (d) => {
    if (d.ente_emittente === "asl") return "ASL";
    if (d.ente_emittente === "medico") return "Medico";
    return d.origine || "—";
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Btn onClick={() => setShowUpload(true)} variant="soft">
          <Upload className="w-4 h-4" />
          Carica cambio terapia
        </Btn>
      </div>
      {loading ? (
        <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>
      ) : docs.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">
          Nessun cambio terapia registrato. Carica il documento firmato dal medico o dall&apos;ASL.
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map(d => (
            <div key={d.id} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 text-sm truncate">{d.file_name}</p>
                  {d.cambio_dettagli && (
                    <p className="text-xs text-slate-500 mt-1 italic">{d.cambio_dettagli}</p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-slate-400">
                    <span>Firmato da: <strong className="text-slate-600">{enteLabel(d)}</strong></span>
                    {d.firmato && <span className="text-emerald-600 font-medium">Documento firmato</span>}
                    {(d.caricato_da_nome || d.createdByName) && (
                      <span>Autore: {d.caricato_da_nome || d.createdByName}</span>
                    )}
                    <span>{formatDate(d.createdAt)}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleDownload(d.id, d.file_name)}
                  className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50"
                  title="Scarica"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <CambioTerapiaDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        fascicoloId={ospite.fascicolo_id}
        ospiteLabel={`${ospite.cognome || ""} ${ospite.nome || ""}`.trim()}
        token={token}
        onSuccess={loadDocs}
      />
    </div>
  );
}

// ==================== RICHIESTE INVIATE TAB ====================

function RichiesteInviateTab({ ospite, token, onRequestTherapy }) {
  const [richieste, setRichieste] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/deblistering/terapie/richieste?fascicolo_id=${ospite.fascicolo_id}`,
          { headers: hdrs(token) }
        );
        setRichieste(r.data.items || []);
      } catch { toast.error("Errore caricamento richieste"); }
      finally { setLoading(false); }
    })();
  }, [ospite.fascicolo_id, token]);

  const STATO_RICH = {
    inviata:           { label: "Inviata",            color: "bg-blue-50 text-blue-700 border-blue-100" },
    consegnata:        { label: "Consegnata",          color: "bg-blue-100 text-blue-800 border-blue-200" },
    documento_ricevuto:{ label: "Documento ricevuto",  color: "bg-amber-50 text-amber-700 border-amber-100" },
    in_elaborazione_ia:{ label: "In elaborazione",  color: "bg-violet-50 text-violet-700 border-violet-100" },
    da_validare:       { label: "Da validare",         color: "bg-amber-50 text-amber-700 border-amber-100" },
    completata:        { label: "Completata",          color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    scaduta:           { label: "Scaduta",             color: "bg-red-50 text-red-700 border-red-100" },
  };

  if (loading) return <div className="py-8 text-center text-slate-400 text-sm">Caricamento...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Btn onClick={onRequestTherapy} variant="soft">
          <Plus className="w-4 h-4" />
          Nuova richiesta
        </Btn>
      </div>
      {richieste.length === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">Nessuna richiesta inviata</div>
      ) : (
        <div className="space-y-3">
          {richieste.map(r => (
            <div key={r.id} className="border border-slate-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 text-sm">{r.tipologia_label || r.tipologia}</p>
                  <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-400">
                    <span className="capitalize">{r.destinatario}</span>
                    <span>{r.email}</span>
                    <span>{formatDate(r.created_at)}</span>
                  </div>
                </div>
                {r.stato && (
                  <span className={`inline-flex px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${(STATO_RICH[r.stato] || STATO_RICH.inviata).color}`}>
                    {(STATO_RICH[r.stato] || STATO_RICH.inviata).label}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== DIALOG RICHIEDI TERAPIA ====================

function getDestinatarioDefaults(ospite, destinatario) {
  if (destinatario === "medico") {
    return {
      email: ospite.medico_email || "",
      nome_destinatario: ospite.medico_nome || "",
    };
  }
  return {
    email: ospite.familiare_email || "",
    nome_destinatario: ospite.familiare_nome || "",
  };
}

function RichiediTerapiaDialog({ ospite, token, onClose, onSuccess }) {
  const [form, setForm] = useState(() => ({
    tipologia: "terapia_iniziale",
    destinatario: "medico",
    messaggio: "",
    ...getDestinatarioDefaults(ospite, "medico"),
  }));
  const [saving, setSaving] = useState(false);

  function handleDestinatarioChange(destinatario) {
    const defaults = getDestinatarioDefaults(ospite, destinatario);
    setForm(f => ({
      ...f,
      destinatario,
      email: defaults.email,
      nome_destinatario: defaults.nome_destinatario,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.email || !form.email.includes("@")) {
      toast.error("Inserisci un indirizzo email valido");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/deblistering/terapie/richiedi`,
        { ...form, fascicolo_id: ospite.fascicolo_id },
        { headers: hdrs(token) }
      );
      toast.success("Richiesta inviata con successo");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h4 className="font-semibold text-slate-900">Richiedi terapia</h4>
            <p className="text-xs text-slate-400 mt-0.5">{ospite.cognome} {ospite.nome}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Tipo richiesta</label>
            <select
              value={form.tipologia}
              onChange={e => setForm(f => ({ ...f, tipologia: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            >
              <option value="terapia_iniziale">Terapia iniziale</option>
              <option value="cambio_terapia">Cambio terapia</option>
              <option value="aggiornamento_terapia">Aggiornamento terapia</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Destinatario</label>
            <select
              value={form.destinatario}
              onChange={e => handleDestinatarioChange(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
            >
              <option value="medico">Medico curante</option>
              <option value="familiare">Familiare / Referente</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Email destinatario *</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder="medico@esempio.it"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Nome destinatario</label>
            <input
              value={form.nome_destinatario}
              onChange={e => setForm(f => ({ ...f, nome_destinatario: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
              placeholder="Dott. Mario Rossi"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Messaggio (opzionale)</label>
            <textarea
              rows={3}
              value={form.messaggio}
              onChange={e => setForm(f => ({ ...f, messaggio: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
              placeholder="Istruzioni aggiuntive per il destinatario..."
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={onClose}>Annulla</Btn>
            <Btn type="submit" disabled={saving}>
              <Send className="w-4 h-4" />
              {saving ? "Invio..." : "Invia richiesta"}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  );
}
